export const RESTRICTION_CODES = {
    VEHICLES_EXTENDED_EDIT: 'vehicles_extended_edit',
    COMMUNICATION: 'communication',
    LOADING_LIST_VIEW: 'loading_list_view',
    LOADING_LIST_EDIT: 'loading_list_edit',
} as const;

export type RestrictionCode = (typeof RESTRICTION_CODES)[keyof typeof RESTRICTION_CODES];

export const RESTRICTION_DISPLAY_ORDER: RestrictionCode[] = [
    RESTRICTION_CODES.VEHICLES_EXTENDED_EDIT,
    RESTRICTION_CODES.COMMUNICATION,
    RESTRICTION_CODES.LOADING_LIST_VIEW,
    RESTRICTION_CODES.LOADING_LIST_EDIT,
];

const VALID_RESTRICTION_CODES = new Set<string>(Object.values(RESTRICTION_CODES));

export function isRestrictionCode(value: string): value is RestrictionCode {
    return VALID_RESTRICTION_CODES.has(value);
}

export function normalizeRestrictions(codes: string[] | undefined | null): RestrictionCode[] {
    if (!codes?.length) {
        return [];
    }

    const selected = new Set(codes.filter(isRestrictionCode));
    return RESTRICTION_DISPLAY_ORDER.filter(code => selected.has(code));
}

export function applyRestrictionToggle(
    current: string[],
    code: RestrictionCode,
    checked: boolean
): RestrictionCode[] {
    const next = new Set(normalizeRestrictions(current));

    if (checked) {
        next.add(code);

        if (code === RESTRICTION_CODES.LOADING_LIST_VIEW) {
            next.add(RESTRICTION_CODES.LOADING_LIST_EDIT);
        }
    } else {
        next.delete(code);

        if (code === RESTRICTION_CODES.LOADING_LIST_VIEW) {
            next.delete(RESTRICTION_CODES.LOADING_LIST_EDIT);
        }
    }

    return RESTRICTION_DISPLAY_ORDER.filter(item => next.has(item));
}

export function isLoadingListEditLocked(restrictions: string[]): boolean {
    return normalizeRestrictions(restrictions).includes(RESTRICTION_CODES.LOADING_LIST_VIEW);
}

export type RestrictionItem = {
    restriction: string;
    isRestricted: boolean;
};

export function getActiveRestrictionCodes(restrictions?: RestrictionItem[] | null): RestrictionCode[] {
    if (!restrictions?.length) {
        return [];
    }

    // Chained over a short list; merging the passes would buy no measurable win.
    // react-doctor-disable-next-line react-doctor/js-combine-iterations
    return restrictions
        .filter(item => item.isRestricted)
        .map(item => item.restriction)
        .filter(isRestrictionCode);
}

export function restrictionsEqual(left: string[], right: string[]): boolean {
    const normalizedLeft = normalizeRestrictions(left);
    const normalizedRight = normalizeRestrictions(right);

    if (normalizedLeft.length !== normalizedRight.length) {
        return false;
    }

    return normalizedLeft.every((code, index) => code === normalizedRight[index]);
}
